import { AlertExtraParams } from '@starsoft/common/models';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * Custom hook to get the default query options and provide a notification function.
 * This hook retrieves the default mutation options from the query client and adds a custom notification function.
 *
 * @returns {Object} - An object containing the default mutation options and the notifyOnSuccess function.
 *
 * Example usage:
 * const { onSuccess, notifyOnSuccess } = useQueryDefaults();
 * notifyOnSuccess({ message: 'Operation successful', duration: 3000 });
 */
export function useQueryDefaults() {
  // Get the query client instance from React Query
  const queryClient = useQueryClient();
  // Retrieve the default mutation options from the query client
  const defaults = queryClient?.getDefaultOptions()?.mutations;

  /**
   * Function to notify on success.
   * This function calls the onSuccess handler from the default mutation options with the provided parameters.
   *
   * @param {Omit<AlertExtraParams, 'type'>} params - The parameters to pass to the onSuccess handler.
   *
   * Example usage:
   * notifyOnSuccess({ message: 'Operation successful', duration: 3000 });
   */
  const notifyOnSuccess = useCallback(
    (params: Omit<AlertExtraParams, 'type'>) => {
      defaults?.onSuccess(undefined, undefined, params);
    },
    [defaults],
  );

  // Return the default mutation options and the notifyOnSuccess function
  return { ...defaults, notifyOnSuccess };
}
